import DateInput from '../../components/DateInput';
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Plus, Wallet, FileSpreadsheet, Download, Printer, Trash2, FileDown, TrendingDown, RefreshCw } from 'lucide-react';
import { printHtml, signatureBlock } from '../../lib/print';
import { toast } from 'sonner';
import { FinanceExpenses } from './FinanceExpenses';
import RecycleBinCard from '../../components/RecycleBinCard';

export default function AdminFinances() {
  const [tab, setTab] = useState('invoices');
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('revenue');  // revenue | expense
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    invoice_type: 'revenue',
    patient_id: '', vendor_id: '', category: 'session',
    currency: 'PLN', country: 'PL', due_date: '',
    items: [{ description: 'Therapy session', quantity: 1, unit_price: 200, vat_rate: 0 }],
  });
  const [summary, setSummary] = useState(null);
  // Iter 132 — Catalogue products (from Store) so clinicians can pick a
  // product for any invoice line. Selecting a product pre-fills the
  // description + unit_price row.
  const [storeProducts, setStoreProducts] = useState([]);
  const [taxCountry, setTaxCountry] = useState('PL');
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [taxReport, setTaxReport] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [writeOffFor, setWriteOffFor] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = () => api.get('/invoices', { params: { invoice_type: invoiceTypeFilter } }).then(r => setInvoices(r.data));
  useEffect(() => {
    load();
    api.get('/patients').then(r => setPatients(r.data));
    api.get('/inventory/vendors').then(r => setVendors(r.data)).catch(() => {});
    api.get('/finance/summary').then(r => setSummary(r.data));
    api.get('/clinic/settings').then(r => setSettings(r.data)).catch(() => {});
    // Iter 132 — Product Catalogue for invoice line-item picker.
    api.get('/store/products').then(r => setStoreProducts(r.data || [])).catch(() => setStoreProducts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceTypeFilter]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0, vat_rate: 0 }] });
  const updateItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: k === 'description' ? v : parseFloat(v) || 0 };
    setForm({ ...form, items });
  };

  const submit = async () => {
    await api.post('/invoices', form);
    setShow(false);
    setForm({ ...form, items: [{ description: 'Therapy session', quantity: 1, unit_price: 200, vat_rate: 0 }] });
    load();
    api.get('/finance/summary').then(r => setSummary(r.data));
  };

  const markPaid = async (id) => {
    const fd = new FormData(); fd.append('status', 'paid');
    await api.put(`/invoices/${id}/status`, fd);
    load();
  };

  const cancelInvoice = async (id) => {
    if (!(await safeConfirm('Delete this invoice? It moves to "Recently deleted" below — restorable for 30 days, then erased permanently.\n\nNote: paid invoices cannot be deleted (use Adjust or Write-off).'))) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Invoice moved to Recently deleted — 30-day undo available at the bottom of this page.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete invoice.');
    }
  };

  const printInvoice = (inv) => {
    const clinicName = (settings?.clinic_name) || 'Physiogenic';
    const clinicAddr = inv.location_address || settings?.address || '';
    const clinicBranch = inv.location_name && inv.location_name !== clinicName ? inv.location_name : null;
    const html = `
      <div class="row">
        <div>
          <h2>Invoice</h2>
          <h1>${inv.invoice_number}</h1>
          <div class="muted">Issued ${(inv.issued_at||'').slice(0,10)} ${inv.due_date ? '· Due '+inv.due_date : ''}</div>
        </div>
        <div style="text-align:right;">
          <div class="clinic">${clinicName}</div>
          ${clinicBranch ? `<div class="muted">${clinicBranch}</div>` : ''}
          ${clinicAddr ? `<div class="muted">${clinicAddr}</div>` : ''}
          <div class="muted">${inv.country} · ${inv.currency}</div>
          <div class="muted" style="margin-top:6px;">Status: <span class="badge">${inv.status.toUpperCase()}</span></div>
        </div>
      </div>
      <div><strong>Bill to:</strong> ${inv.patient_name || inv.vendor_name || '—'}</div>
      <table>
        <thead><tr><th>Description</th><th>Qty</th><th>Unit price</th><th>Total</th></tr></thead>
        <tbody>
          ${inv.items.map(it => `<tr>
            <td>${it.description}</td>
            <td>${it.quantity}</td>
            <td>${it.unit_price.toFixed(2)}</td>
            <td>${(it.unit_price * it.quantity).toFixed(2)} ${inv.currency}</td>
          </tr>`).join('')}
        </tbody>
      </table>
      <div class="totals">
        <div class="grand">Total: ${inv.total.toFixed(2)} ${inv.currency}</div>
      </div>
      <div style="margin-top:14px;color:#6B7A8E;font-size:11px;font-style:italic;">
        Physiotherapy services are VAT-exempt under Polish VAT Act art. 43(1)(19).
      </div>
      ${inv.notes ? `<div style="margin-top:20px;color:#4A524A;font-size:12px;"><strong>Notes:</strong> ${inv.notes}</div>` : ''}
      ${signatureBlock({ leftLabel: 'Clinician / Provider', rightLabel: 'Patient', showStamp: true })}
      <div class="footer">${clinicName} &middot; HIPAA &amp; GDPR compliant &middot; This invoice is generated electronically.</div>
    `;
    toast('Opening print dialog…');
    printHtml(html, inv.invoice_number);
  };

  const downloadInvoicePdf = async (inv) => {
    const t = toast.loading('Generating PDF…');
    try {
      const r = await api.get(`/invoices/${inv.invoice_id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Force a download — opening a blob URL in a new tab is unreliable
      // (Chrome blanks the tab when the PDF viewer plugin is off; Safari
      // strips blob URLs cross-context). A direct download works universally.
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(inv.invoice_number || 'invoice').replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(`PDF downloaded: ${inv.invoice_number}`, { id: t });
    } catch (e) {
      // axios `responseType:'blob'` returns the error body as a Blob too —
      // parse it as text so we can surface the real reason.
      let msg = e.message || 'PDF export failed';
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { msg = JSON.parse(txt).detail || txt; }
          catch (_) { msg = txt; }
        } else {
          msg = e.response?.data?.detail || msg;
        }
      } catch (_) { /* ignore */ }
      toast.error('PDF export failed: ' + msg, { id: t });
    }
  };

  const runTaxReport = () => {
    api.get('/finance/tax-report', { params: { year: taxYear, country: taxCountry } })
      .then(r => setTaxReport(r.data));
  };

  const exportCSV = () => {
    if (!taxReport) return;
    const rows = [['Invoice', 'Patient', 'Issued', 'Total', 'Currency']];
    taxReport.rows.forEach(r => rows.push([r.invoice_number, r.patient_name, r.issued_at, r.gross, r.currency]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `revenue_report_PL_${taxYear}.csv`;
    a.click();
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">REVENUE & TAX</div>
      <h1 className="text-4xl font-heading font-bold tracking-tight mb-6">Finances</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Revenue" value={summary ? summary.total_revenue.toFixed(2) : '—'} />
        <Stat label="Outstanding" value={summary ? summary.total_outstanding.toFixed(2) : '—'} />
        <Stat label="Paid invoices" value={summary?.invoices_paid ?? '—'} />
        <Stat label="Open invoices" value={summary?.invoices_unpaid ?? '—'} />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={()=>setTab('invoices')} className={`px-4 py-2 text-sm rounded-md ${tab==='invoices'?'bg-brand text-white':'border border-soft'}`} data-testid="tab-invoices">Invoices</button>
        <button onClick={()=>setTab('payroll')} className={`px-4 py-2 text-sm rounded-md ${tab==='payroll'?'bg-brand text-white':'border border-soft'}`} data-testid="tab-payroll">Payroll & expenses</button>
        <button onClick={()=>setTab('taxes')} className={`px-4 py-2 text-sm rounded-md ${tab==='taxes'?'bg-brand text-white':'border border-soft'}`} data-testid="tab-taxes">Tax reports</button>
      </div>

      {tab === 'payroll' && <FinanceExpenses/>}

      {tab === 'invoices' && (
        <>
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <select className="input max-w-[200px]" value={invoiceTypeFilter}
                      onChange={e=>setInvoiceTypeFilter(e.target.value)} data-testid="inv-type-filter">
                <option value="revenue">Revenue invoices</option>
                <option value="expense">Expense invoices</option>
              </select>
              <a className="btn-secondary text-sm inline-flex items-center gap-2"
                 href={(process.env.REACT_APP_BACKEND_URL || '') + `/api/invoices/export/excel?invoice_type=${invoiceTypeFilter}`}
                 target="_blank" rel="noopener noreferrer" data-testid="export-excel">
                <FileSpreadsheet className="w-4 h-4"/> Export Excel
              </a>
            </div>
            <button onClick={() => { setShow(!show); setForm(f=>({...f, invoice_type: invoiceTypeFilter})); }} className="btn-primary inline-flex items-center gap-2" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4" /> {show ? 'Close' : (invoiceTypeFilter === 'expense' ? 'New expense' : 'New invoice')}
            </button>
          </div>

          {show && (
            <div className="admin-card mb-6">
              <h3 className="font-heading font-semibold mb-4">
                Create {form.invoice_type === 'expense' ? 'expense' : 'revenue'} invoice
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-fg-muted">Type</label>
                  <select className="input" value={form.invoice_type}
                          onChange={e=>setForm({...form, invoice_type: e.target.value, patient_id:'', vendor_id:''})}
                          data-testid="inv-type">
                    <option value="revenue">Revenue (patient billing)</option>
                    <option value="expense">Expense (vendor bill)</option>
                  </select>
                </div>
                {form.invoice_type === 'revenue' ? (
                  <div>
                    <label className="text-xs text-fg-muted">Patient</label>
                    <select className="input" value={form.patient_id} onChange={e=>setForm({...form, patient_id: e.target.value})} data-testid="inv-patient">
                      <option value="">Select…</option>
                      {patients.map(p => <option key={p.user_id} value={p.user_id}>{p.full_name}</option>)}
                    </select>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="text-xs text-fg-muted">Vendor</label>
                      <select className="input" value={form.vendor_id} onChange={e=>setForm({...form, vendor_id: e.target.value})} data-testid="inv-vendor">
                        <option value="">Select…</option>
                        {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-fg-muted">Category</label>
                      <select className="input" value={form.category} onChange={e=>setForm({...form, category: e.target.value})}>
                        <option>rent</option><option>utilities</option><option>supplies</option><option>salary</option>
                        <option>marketing</option><option>equipment</option><option>other</option>
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className="text-xs text-fg-muted">Country</label>
                  <select className="input" value={form.country} onChange={e=>setForm({...form, country: e.target.value})} data-testid="inv-country" disabled>
                    <option value="PL">Poland</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-fg-muted">Currency</label>
                  <select className="input" value={form.currency} onChange={e=>setForm({...form, currency: e.target.value})} data-testid="inv-currency">
                    <option>PLN</option><option>EUR</option><option>USD</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-fg-muted">Due date</label>
                  <DateInput value={form.due_date} onChange={e=>setForm({...form, due_date: e.target.value})} data-testid="inv-due" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-6">
                      <input className="input" placeholder="Description" value={it.description}
                              onChange={e=>updateItem(i,'description',e.target.value)} data-testid={`item-desc-${i}`} />
                      {/* Iter 132 — Optional catalogue picker. Selecting a
                          product from the catalogue auto-fills the
                          description + unit_price for this line so the
                          clinician can quickly add store items to a
                          patient's invoice (braces, taping, orthotics, etc). */}
                      {storeProducts.length > 0 && (
                        <select
                          className="input mt-1 text-xs"
                          value=""
                          onChange={e => {
                            const p = storeProducts.find(sp => sp.product_id === e.target.value);
                            if (!p) return;
                            const items = [...form.items];
                            items[i] = {
                              ...items[i],
                              description: p.name + (p.sku ? ` (SKU ${p.sku})` : ''),
                              unit_price: parseFloat(p.price) || 0,
                              vat_rate: 0,
                            };
                            setForm({ ...form, items, currency: p.currency || form.currency });
                          }}
                          data-testid={`item-picker-${i}`}
                        >
                          <option value="">+ Pick from catalogue…</option>
                          {storeProducts.map(p => (
                            <option key={p.product_id} value={p.product_id}>
                              {p.name} — {p.currency || 'PLN'} {p.price}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <input className="input col-span-2" type="number" placeholder="Qty" value={it.quantity}
                            onChange={e=>updateItem(i,'quantity',e.target.value)} />
                    <input className="input col-span-4" type="number" placeholder="Unit price" value={it.unit_price}
                            onChange={e=>updateItem(i,'unit_price',e.target.value)} />
                  </div>
                ))}
                <button onClick={addItem} className="text-xs text-brand inline-flex items-center gap-1" data-testid="add-item">
                  <Plus className="w-3 h-3" /> Add line
                </button>
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={submit} className="btn-primary" disabled={!form.patient_id} data-testid="inv-save">Save invoice</button>
              </div>
            </div>
          )}

          <div className="admin-card">
            <table>
              <thead><tr><th>Number</th><th>{invoiceTypeFilter === 'expense' ? 'Vendor / Category' : 'Patient'}</th><th>Total</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {invoices.map(i => (
                  <tr key={i.invoice_id} data-testid={`invoice-row-${i.invoice_id}`}>
                    <td className="font-mono text-xs">{i.invoice_number}</td>
                    <td>
                      {i.invoice_type === 'expense'
                        ? <>{i.vendor_name || '—'} <span className="text-[10px] text-fg-muted">· {i.category}</span></>
                        : (i.patient_name || '—')}
                    </td>
                    <td className="font-medium">
                      {i.total.toFixed(2)} {i.currency}
                      {(i.write_off_amount || 0) > 0 && <div className="text-[10px] text-warn">-{i.write_off_amount.toFixed(2)} write-off</div>}
                      {(i.adjustment_amount || 0) !== 0 && <div className="text-[10px] text-fg-muted">{i.adjustment_amount > 0 ? '+' : ''}{i.adjustment_amount.toFixed(2)} adj.</div>}
                    </td>
                    <td><StatusBadge s={i.status} /></td>
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={()=>printInvoice(i)} className="text-xs text-brand inline-flex items-center gap-1" data-testid={`print-${i.invoice_id}`}>
                          <Printer className="w-3.5 h-3.5"/> Print
                        </button>
                        <button onClick={()=>downloadInvoicePdf(i)} className="text-xs text-brand inline-flex items-center gap-1" data-testid={`pdf-${i.invoice_id}`}>
                          <FileDown className="w-3.5 h-3.5"/> PDF
                        </button>
                        {i.status !== 'paid' && i.status !== 'cancelled' && i.status !== 'written_off' && (
                          <button onClick={()=>markPaid(i.invoice_id)} className="text-xs text-brand" data-testid={`mark-paid-${i.invoice_id}`}>Mark paid</button>
                        )}
                        {i.status !== 'cancelled' && i.status !== 'written_off' && (
                          <button onClick={()=>setWriteOffFor(i)} className="text-xs text-warn inline-flex items-center gap-1" data-testid={`write-off-${i.invoice_id}`}>
                            <TrendingDown className="w-3.5 h-3.5"/> Write-off
                          </button>
                        )}
                        {i.status !== 'cancelled' && (
                          <button onClick={()=>setAdjustFor(i)} className="text-xs text-brand inline-flex items-center gap-1" data-testid={`adjust-${i.invoice_id}`}>
                            <RefreshCw className="w-3.5 h-3.5"/> Adjust
                          </button>
                        )}
                        {i.status !== 'cancelled' && (
                          <button onClick={()=>cancelInvoice(i.invoice_id)} className="text-xs text-[#B83A3A] inline-flex items-center gap-1" data-testid={`cancel-inv-${i.invoice_id}`}>
                            <Trash2 className="w-3.5 h-3.5"/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!invoices.length && <tr><td colSpan="5" className="text-center py-8 text-fg-muted">No invoices.</td></tr>}
              </tbody>
            </table>
          </div>

          {writeOffFor && <AdjustmentModal kind="write-off" invoice={writeOffFor} onClose={()=>setWriteOffFor(null)} onDone={()=>{ load(); setWriteOffFor(null); }}/>}
          {adjustFor && <AdjustmentModal kind="adjust" invoice={adjustFor} onClose={()=>setAdjustFor(null)} onDone={()=>{ load(); setAdjustFor(null); }}/>}
        </>
      )}

      {tab === 'taxes' && (
        <div className="admin-card">
          <div className="flex flex-wrap items-end gap-3 mb-6">
            <div>
              <label className="text-xs text-fg-muted">Country</label>
              <select className="input" value={taxCountry} onChange={e=>setTaxCountry(e.target.value)} data-testid="tax-country">
                <option value="PL">🇵🇱 Poland</option>
                <option value="DE">🇩🇪 Germany</option>
                <option value="FR">🇫🇷 France</option>
                <option value="ES">🇪🇸 Spain</option>
                <option value="IT">🇮🇹 Italy</option>
                <option value="UK">🇬🇧 United Kingdom</option>
                <option value="US">🇺🇸 United States</option>
                <option value="CA">🇨🇦 Canada</option>
                <option value="AU">🇦🇺 Australia</option>
                <option value="IE">🇮🇪 Ireland</option>
                <option value="NL">🇳🇱 Netherlands</option>
                <option value="BE">🇧🇪 Belgium</option>
                <option value="AT">🇦🇹 Austria</option>
                <option value="CH">🇨🇭 Switzerland</option>
                <option value="PT">🇵🇹 Portugal</option>
                <option value="SE">🇸🇪 Sweden</option>
                <option value="KN">🇰🇳 Saint Kitts and Nevis</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-fg-muted">Year</label>
              <input className="input" type="number" value={taxYear} onChange={e=>setTaxYear(e.target.value)} data-testid="tax-year" />
            </div>
            <button onClick={runTaxReport} className="btn-primary inline-flex items-center gap-2" data-testid="run-tax-report">
              <FileSpreadsheet className="w-4 h-4" /> Generate
            </button>
            {taxReport && (
              <button onClick={exportCSV} className="btn-secondary inline-flex items-center gap-2" data-testid="export-csv">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            )}
            {taxReport && (
              <button onClick={()=>window.print()} className="btn-secondary inline-flex items-center gap-2" data-testid="print-tax-report">
                <Printer className="w-4 h-4" /> Print
              </button>
            )}
          </div>

          {taxReport && (
            <div>
              <div className="surface border border-soft p-4 mb-4">
                <div className="font-heading font-semibold mb-1">{taxReport.preset?.name}</div>
                <div className="text-xs text-fg-muted">{taxReport.preset?.tax_authority} · {taxReport.preset?.filing}</div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><div className="text-xs text-fg-muted">Invoiced total</div><div className="text-2xl font-heading font-bold">{taxReport.total_gross.toFixed(2)}</div></div>
                  <div><div className="text-xs text-fg-muted">Paid invoices</div><div className="text-2xl font-heading font-bold">{taxReport.rows?.length || 0}</div></div>
                </div>
                <div className="text-[11px] text-fg-muted italic mt-3">
                  Physiotherapy services are VAT-exempt (PL VAT Act art. 43(1)(19)). This report tracks gross revenue only.
                </div>
              </div>
              <table>
                <thead><tr><th>Invoice</th><th>Patient</th><th>Issued</th><th>Total</th></tr></thead>
                <tbody>
                  {taxReport.rows.map(r => (
                    <tr key={r.invoice_number}>
                      <td className="font-mono text-xs">{r.invoice_number}</td>
                      <td>{r.patient_name}</td>
                      <td className="text-xs">{r.issued_at?.slice(0,10)}</td>
                      <td>{r.gross.toFixed(2)} {r.currency}</td>
                    </tr>
                  ))}
                  {!taxReport.rows.length && <tr><td colSpan="4" className="text-center py-6 text-fg-muted">No paid invoices for this period.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <RecycleBinCard kind="invoice" noun="Invoice" onRestored={load}/>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="admin-card">
      <Wallet className="w-5 h-5 text-brand mb-3" strokeWidth={1.6} />
      <div className="text-3xl font-heading font-bold">{value}</div>
      <div className="text-xs text-fg-muted mt-1">{label}</div>
    </div>
  );
}

function StatusBadge({ s }) {
  const cls = s === 'paid' ? 'badge-success'
              : s === 'unpaid' ? 'badge-warn'
              : s === 'written_off' ? 'badge-neutral'
              : 'badge-error';
  return <span className={cls}>{s.replace('_', ' ')}</span>;
}

function AdjustmentModal({ kind, invoice, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const isWriteOff = kind === 'write-off';
  const title = isWriteOff ? 'Write-off invoice' : 'Adjust invoice';
  const submit = async () => {
    if (!reason || !amount) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('amount', amount); fd.append('reason', reason);
      const url = isWriteOff
        ? `/invoices/${invoice.invoice_id}/write-off`
        : `/invoices/${invoice.invoice_id}/adjust`;
      await api.post(url, fd);
      onDone();
    } catch (e) {
      alert('Failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="adjustment-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b border-soft">
          <h2 className="font-heading font-semibold">{title}</h2>
          <p className="text-xs text-fg-muted mt-1">{invoice.invoice_number} · {invoice.total.toFixed(2)} {invoice.currency}</p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">
              {isWriteOff ? 'Amount to write-off (≤ invoice total)' : 'Adjustment amount (+/-)'}
            </div>
            <input className="input" type="number" step="0.01" value={amount}
                   onChange={e=>setAmount(e.target.value)} data-testid="adj-amount"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Reason</div>
            <textarea className="input min-h-[80px]" value={reason} onChange={e=>setReason(e.target.value)}
                      data-testid="adj-reason" placeholder={isWriteOff ? 'e.g. uncollectible' : 'e.g. consolidation, correction'}/>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={submit} disabled={saving || !amount || !reason}
                    className="btn-primary text-sm" data-testid="adj-save">
              {saving ? 'Applying…' : (isWriteOff ? 'Write-off' : 'Apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
